import { useState } from 'react';
import { useHuntMarketplace, submitHuntToMarketplace, marketplaceHuntAction, type MarketplaceHunt } from '../../hooks/useSecOps';

function RatingStars({ rating, onRate }: { rating: number; onRate?: (n: number) => void }) {
  const stars = Math.round(rating / 20); // 0-100 → 0-5
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          onClick={() => onRate?.(n)}
          disabled={!onRate}
          className={`text-sm ${n <= stars ? 'text-amber-400' : 'text-gray-300'} ${onRate ? 'cursor-pointer hover:text-amber-500' : 'cursor-default'}`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  const colors: Record<string, string> = {
    critical: 'bg-red-100 text-red-700',
    high: 'bg-orange-100 text-orange-700',
    medium: 'bg-amber-100 text-amber-700',
    low: 'bg-blue-100 text-blue-700',
  };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${colors[severity] ?? 'bg-gray-100 text-gray-700'}`}>
      {severity}
    </span>
  );
}

export default function HuntMarketplacePage() {
  const { data: response, loading } = useHuntMarketplace();
  const hunts = (response as { data?: MarketplaceHunt[] })?.data ?? [];
  const collectiveConfigured = (response as { collective_configured?: boolean })?.collective_configured ?? false;

  const [showSubmit, setShowSubmit] = useState(false);
  const [showDetail, setShowDetail] = useState<MarketplaceHunt | null>(null);
  const [filter, setFilter] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Submit form state
  const [submitForm, setSubmitForm] = useState({ name: '', description: '', kql_query: '', severity: 'medium', tags: '', share: false });

  const filtered = hunts.filter(h =>
    !filter || h.name.toLowerCase().includes(filter.toLowerCase()) ||
    h.tags.some(t => t.toLowerCase().includes(filter.toLowerCase())) ||
    h.mitre_techniques.some(t => t.toLowerCase().includes(filter.toLowerCase()))
  );

  const handleSubmit = async () => {
    if (!submitForm.name || !submitForm.kql_query) return;
    setSubmitting(true);
    try {
      await submitHuntToMarketplace({
        name: submitForm.name,
        description: submitForm.description,
        kql_query: submitForm.kql_query,
        severity: submitForm.severity,
        tags: submitForm.tags.split(',').map(t => t.trim()).filter(Boolean),
        share_to_collective: submitForm.share,
      });
      setShowSubmit(false);
      setSubmitForm({ name: '', description: '', kql_query: '', severity: 'medium', tags: '', share: false });
      window.location.reload();
    } finally {
      setSubmitting(false);
    }
  };

  const handleRate = async (huntId: string, rating: number) => {
    await marketplaceHuntAction(huntId, 'rate', rating);
  };

  const handleImport = async (huntId: string) => {
    await marketplaceHuntAction(huntId, 'import');
  };

  if (loading && hunts.length === 0) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Threat Hunt Marketplace</h1>
        <div className="animate-pulse space-y-3">
          {[1, 2, 3, 4].map(i => <div key={i} className="bg-gray-200 h-32 rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Threat Hunt Marketplace</h1>
          <p className="text-xs text-gray-400 mt-1">
            Community-contributed KQL hunting queries
            {collectiveConfigured && ' via Collective'}
          </p>
        </div>
        <button
          onClick={() => setShowSubmit(true)}
          className="px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          Submit Hunt Query
        </button>
      </div>

      {/* Search */}
      <input
        type="text"
        value={filter}
        onChange={e => setFilter(e.target.value)}
        placeholder="Search by name, tag, or MITRE technique..."
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />

      {/* Hunt cards */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-sm text-gray-400">{filter ? 'No matching hunts found' : 'No community hunts yet. Submit the first one!'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((hunt: MarketplaceHunt) => (
            <div
              key={hunt.id}
              className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setShowDetail(hunt)}
            >
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-900 flex-1">{hunt.name}</h3>
                <div className="flex items-center gap-2 ml-2">
                  <SeverityBadge severity={hunt.severity} />
                  {hunt.source === 'collective' && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full">Collective</span>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-3 line-clamp-2">{hunt.description}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <RatingStars rating={hunt.effectiveness_rating} />
                  <span className="text-[10px] text-gray-400">{hunt.total_ratings} ratings</span>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-gray-400">
                  <span>{hunt.total_runs} runs</span>
                  {hunt.environments_detected > 0 && (
                    <span className="text-green-600 font-medium">{hunt.environments_detected} envs detected</span>
                  )}
                </div>
              </div>
              {hunt.mitre_techniques.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {hunt.mitre_techniques.slice(0, 4).map(t => (
                    <span key={t} className="text-[10px] px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded font-mono">{t}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Detail Slide-out */}
      {showDetail && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setShowDetail(null)}>
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative w-full max-w-lg bg-white shadow-xl overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900">{showDetail.name}</h2>
                <button onClick={() => setShowDetail(null)} className="text-gray-400 hover:text-gray-600 text-lg">×</button>
              </div>
              <div className="flex items-center gap-2">
                <SeverityBadge severity={showDetail.severity} />
                <RatingStars rating={showDetail.effectiveness_rating} onRate={(n) => handleRate(showDetail.id, n)} />
                <span className="text-[10px] text-gray-400">{showDetail.total_ratings} ratings</span>
              </div>
              <p className="text-sm text-gray-600">{showDetail.description}</p>

              <div>
                <p className="text-xs font-medium text-gray-700 mb-1">KQL Query</p>
                <pre className="bg-gray-900 text-green-400 text-xs p-3 rounded-lg overflow-x-auto whitespace-pre-wrap font-mono">{showDetail.kql_query}</pre>
              </div>

              {showDetail.mitre_techniques.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-1">MITRE Techniques</p>
                  <div className="flex flex-wrap gap-1">
                    {showDetail.mitre_techniques.map(t => (
                      <span key={t} className="text-[10px] px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full font-mono">{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {showDetail.tags.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-1">Tags</p>
                  <div className="flex flex-wrap gap-1">
                    {showDetail.tags.map(t => (
                      <span key={t} className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{t}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-lg font-bold text-gray-900">{showDetail.total_runs}</p>
                  <p className="text-[10px] text-gray-400">Total Runs</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-lg font-bold text-gray-900">{showDetail.true_positive_rate}%</p>
                  <p className="text-[10px] text-gray-400">True Positive</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-lg font-bold text-green-600">{showDetail.environments_detected}</p>
                  <p className="text-[10px] text-gray-400">Envs Detected</p>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                {showDetail.source === 'collective' && (
                  <button
                    onClick={() => handleImport(showDetail.id)}
                    className="flex-1 px-3 py-2 text-xs font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    Import to My Hunts
                  </button>
                )}
                <button
                  onClick={() => { setShowDetail(null); }}
                  className="flex-1 px-3 py-2 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Submit Modal */}
      {showSubmit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setShowSubmit(false)}>
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-900">Submit Hunt Query</h2>
            <div>
              <label className="text-xs font-medium text-gray-700">Name</label>
              <input value={submitForm.name} onChange={e => setSubmitForm(f => ({ ...f, name: e.target.value }))}
                className="w-full mt-1 px-3 py-2 text-sm border rounded-lg" placeholder="e.g., Detect LOLBIN abuse" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Description</label>
              <textarea value={submitForm.description} onChange={e => setSubmitForm(f => ({ ...f, description: e.target.value }))}
                className="w-full mt-1 px-3 py-2 text-sm border rounded-lg" rows={2} placeholder="What does this query detect?" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">KQL Query</label>
              <textarea value={submitForm.kql_query} onChange={e => setSubmitForm(f => ({ ...f, kql_query: e.target.value }))}
                className="w-full mt-1 px-3 py-2 text-sm border rounded-lg font-mono bg-gray-50" rows={5} placeholder="SecurityEvent | where ..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-700">Severity</label>
                <select value={submitForm.severity} onChange={e => setSubmitForm(f => ({ ...f, severity: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 text-sm border rounded-lg">
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Tags (comma-separated)</label>
                <input value={submitForm.tags} onChange={e => setSubmitForm(f => ({ ...f, tags: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 text-sm border rounded-lg" placeholder="ransomware, c2" />
              </div>
            </div>
            {collectiveConfigured && (
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input type="checkbox" checked={submitForm.share} onChange={e => setSubmitForm(f => ({ ...f, share: e.target.checked }))} />
                Share anonymously with the Collective community
              </label>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowSubmit(false)} className="px-3 py-2 text-xs text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleSubmit} disabled={submitting || !submitForm.name || !submitForm.kql_query}
                className="px-4 py-2 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
